import { Box, BoxProps } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { ReactElement, useEffect, useRef, useMemo } from 'react';

import { useMultiStyleConfig } from '../../system/hooks';

import { useSideBarContext } from './SideBarContext';
import { ToggleActiveNavItem } from './effects';
import DisplayNames from './enums';

export type SideBarItemProps = BoxProps & SideBarHOCProps;
export interface SideBarHOCProps {
  isActive?: boolean;
}

const SideBarItem = (props: SideBarItemProps): ReactElement => {
  const { isActive, onClick, ...rest } = props;

  const sideBarItemEl = useRef();
  const { isCurrentActiveItem, setCurrentActiveItem } = useSideBarContext();

  const isCurrentlyActive = useMemo(() => isCurrentActiveItem(sideBarItemEl), [isCurrentActiveItem]);
  const { sideBarItem: themeStyles } = useMultiStyleConfig('SideBar', {
    ...props,
    isActive: isCurrentlyActive
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(ToggleActiveNavItem(sideBarItemEl, isActive), [isActive]);

  const handleOnClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setCurrentActiveItem(sideBarItemEl);

    if (onClick) {
      onClick(evt);
    }
  };

  const computedProps = {
    ...(themeStyles as BoxProps),
    'aria-label': 'sidebar item',
    as: 'button',
    onClick: handleOnClick,
    ref: sideBarItemEl,
    ...rest
  } as BoxProps;

  return <Box data-testid="sidebar-item" {...computedProps} />;
};

SideBarItem.propTypes = {
  isActive: PropTypes.bool
};
SideBarItem.defaultProps = {
  isActive: false
};
SideBarItem.displayName = DisplayNames.SideBarItem;

export default SideBarItem;
